
import React, { useState, useMemo } from 'react';
import type { ShoppingCart, Language, Ingredient, ActiveShoppingCart } from '../types';
import CartIcon from './icons/CartIcon';
import PlusIcon from './icons/PlusIcon';
import MeatIcon from './icons/MeatIcon';
import LeafIcon from './icons/LeafIcon';
import BeakerIcon from './icons/BeakerIcon';
import UtensilsIcon from './icons/UtensilsIcon';
import SearchField from './SearchField';
import SearchIcon from './icons/SearchIcon';
import EllipsisVerticalIcon from './icons/EllipsisVerticalIcon';
import XIcon from './icons/XIcon';
import { getTranslation, isRTL } from '../utils/translations';
import { toListPillarId } from '../utils/shoppingCategories';
import { addManualQuantity } from '../utils/activeCartSources';
import { findActiveCartItemForListIngredient } from '../utils/activeCartMatch';

interface ListTabProps {
    shoppingCarts: ShoppingCart[];
    onAddCart: () => void;
    onViewCart: (cart: ShoppingCart) => void;
    onEditCart: (cart: ShoppingCart) => void;
    onDeleteCart: (cartId: string) => void;
    canManage: boolean;
    language?: Language;
    activeCart?: ActiveShoppingCart | null;
    onUpdateActiveCart?: (cart: ActiveShoppingCart) => Promise<void>;
    onUpdateCart?: (cart: ShoppingCart) => Promise<void>;
}

const getCategoryDetails = (category: string) => {
    const cat = (category || 'General').toLowerCase();
    switch (cat) {
        case 'meat':
            return { 
                icon: <MeatIcon className="w-5 h-5" />, 
                bg: 'bg-rose-50 dark:bg-rose-900/20', 
                text: 'text-rose-600 dark:text-rose-400',
                tagBg: 'bg-rose-100 dark:bg-rose-900/40',
                tagText: 'text-rose-700 dark:text-rose-300'
            };
        case 'vegetables':
        case 'fruits':
            return { 
                icon: <LeafIcon className="w-5 h-5" />, 
                bg: 'bg-emerald-50 dark:bg-emerald-900/20', 
                text: 'text-emerald-600 dark:text-emerald-400',
                tagBg: 'bg-emerald-100 dark:bg-emerald-900/40',
                tagText: 'text-emerald-700 dark:text-emerald-300'
            };
        case 'dairy':
            return { 
                icon: <span className="text-lg leading-none">🥛</span>,
                bg: 'bg-blue-50 dark:bg-blue-900/20', 
                text: 'text-blue-600 dark:text-blue-400',
                tagBg: 'bg-blue-100 dark:bg-blue-900/40',
                tagText: 'text-blue-700 dark:text-blue-300'
            };
        case 'cleaning':
        case 'toiletries':
            return { 
                icon: <BeakerIcon className="w-5 h-5" />, 
                bg: 'bg-purple-50 dark:bg-purple-900/20', 
                text: 'text-purple-600 dark:text-purple-400',
                tagBg: 'bg-purple-100 dark:bg-purple-900/40',
                tagText: 'text-purple-700 dark:text-purple-300'
            };
        case 'pantry':
        case 'canned':
        case 'generalfood':
            return { 
                icon: <UtensilsIcon className="w-5 h-5" />, 
                bg: 'bg-orange-50 dark:bg-orange-900/20', 
                text: 'text-orange-600 dark:text-orange-400',
                tagBg: 'bg-orange-100 dark:bg-orange-900/40',
                tagText: 'text-orange-700 dark:text-orange-300'
            };
        default:
            return { 
                icon: <CartIcon className="w-5 h-5" />, 
                bg: 'bg-slate-100 dark:bg-slate-800', 
                text: 'text-slate-500 dark:text-slate-400',
                tagBg: 'bg-slate-200 dark:bg-slate-700',
                tagText: 'text-slate-600 dark:text-slate-300'
            };
    }
};

const MoveItemModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    carts: ShoppingCart[];
    onSelect: (cartId: string) => void;
    language?: Language;
}> = ({ isOpen, onClose, carts, onSelect, language }) => {
    const t = (k: any) => getTranslation(language, k);
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[500] p-4 animate-fade-in" onClick={onClose}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
                    <h3 className="font-black text-slate-800 dark:text-slate-100 text-sm uppercase tracking-widest">{t('moveItem')}</h3>
                    <button onClick={onClose}><XIcon className="w-5 h-5 text-slate-400" /></button>
                </div>
                <div className="p-2 overflow-y-auto max-h-[60vh] bg-white dark:bg-slate-900">
                    {carts.map(cart => (
                        <button
                            key={cart.id}
                            onClick={() => onSelect(cart.id)}
                            className="w-full text-start p-3 hover:bg-slate-50 dark:hover:bg-slate-800 border-b border-slate-50 dark:border-slate-800 last:border-0 flex items-center gap-3 transition-colors"
                        >
                            <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg text-slate-500 dark:text-slate-400">
                                <CartIcon className="w-4 h-4" />
                            </div>
                            <span className="font-bold text-slate-700 dark:text-slate-300 text-sm">{cart.name}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

const SearchResultItem: React.FC<{
    item: Ingredient & { cartId: string; cartName: string; cartCategory: string };
    onAdd: () => void;
    onDelete: () => void;
    onMove: () => void;
    isAdding: boolean;
    activeCartItem?: ActiveShoppingCart['items'][number];
    language?: Language;
}> = ({ item, onAdd, onDelete, onMove, isAdding, activeCartItem, language }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const t = (k: any) => getTranslation(language, k);
    const style = getCategoryDetails(item.cartCategory);
    const isInCart = !!activeCartItem;
    const isDoneInCart = activeCartItem?.done === true;

    const cartButtonClass = isInCart && !isDoneInCart
        ? 'h-9 px-4 bg-brand-sage text-white hover:bg-brand-sage/90 rounded-full flex items-center gap-1.5 font-black text-[10px] uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50 shadow-sm shadow-brand-sage/20'
        : isDoneInCart
            ? 'h-9 px-4 bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 rounded-full flex items-center gap-1.5 font-black text-[10px] uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50'
            : 'h-9 px-4 bg-orange-50 text-orange-600 hover:bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400 rounded-full flex items-center gap-1.5 font-black text-[10px] uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50 shadow-sm';

    const cartButtonLabel = isInCart && !isDoneInCart
        ? t('inActiveCart')
        : isDoneInCart
            ? t('done')
            : t('add');

    return (
        <div className="w-full bg-white dark:bg-slate-900 border-b border-gray-50 dark:border-slate-800 p-3 flex flex-row items-center justify-between transition-colors hover:bg-slate-50/50 dark:hover:bg-slate-800/30 gap-3">
            {/* Dots Menu moved to be FIRST for visual "start" position (Right in RTL, Left in LTR) */}
            <div className="relative shrink-0 z-20">
                <button 
                    onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
                    className="p-2 text-slate-300 dark:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors active:scale-90"
                >
                    <EllipsisVerticalIcon className="w-5 h-5" />
                </button>
                
                {menuOpen && (
                    <>
                        <div className="fixed inset-0 z-[100]" onClick={() => setMenuOpen(false)} />
                        {/* 
                            Menu Positioning:
                            In RTL, the dots are at the START (right). We want menu aligned to the right edge growing left.
                            In LTR, dots at the START (left). We want menu aligned to left edge growing right.
                        */}
                        <div className="absolute top-full mt-1 ltr:left-0 rtl:right-0 bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 shadow-2xl rounded-xl py-1 min-w-[160px] z-[110] animate-fade-in flex flex-col ring-1 ring-black/5 overflow-visible">
                            <button 
                                onClick={() => { setMenuOpen(false); onMove(); }}
                                className="w-full text-start px-4 py-3 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-3 transition-colors"
                            >
                                <span className="text-base">📂</span> 
                                <span>{t('moveItem')}</span>
                            </button>
                            <button 
                                onClick={() => { setMenuOpen(false); onDelete(); }}
                                className="w-full text-start px-4 py-3 text-xs font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-3 transition-colors"
                            >
                                <span className="text-base">🗑️</span> 
                                <span>{t('deleteFromList')}</span>
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Info Section (Middle) */}
            <div className="flex flex-col gap-1 min-w-0 flex-grow">
                <span className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate">{item.name}</span>
                <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-md w-fit ${style.bg}`}>
                    <span className={style.text}>{style.icon}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-tight ${style.text}`}>{item.cartName}</span>
                </div>
            </div>

            {/* Add Button (End) */}
            <div className="shrink-0">
                <button 
                    onClick={onAdd}
                    disabled={isAdding}
                    className={cartButtonClass}
                    title={cartButtonLabel}
                >
                    {isAdding ? (
                        <div className="w-3.5 h-3.5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                    ) : (
                        <CartIcon className={`w-3.5 h-3.5 ${isInCart && !isDoneInCart ? 'stroke-[2]' : 'stroke-[2.5]'}`} />
                    )}
                    <span>{cartButtonLabel}</span>
                </button>
            </div>
        </div>
    );
};

const ListTab: React.FC<ListTabProps> = ({ 
    shoppingCarts, onAddCart, onViewCart, onEditCart, onDeleteCart, canManage, language,
    activeCart, onUpdateActiveCart, onUpdateCart
}) => {
    const t = (key: any) => getTranslation(language, key);
    const [searchQuery, setSearchQuery] = useState('');
    const [isAddingId, setIsAddingId] = useState<string | null>(null);
    const [moveItemData, setMoveItemData] = useState<{ ingredient: Ingredient, sourceCartId: string } | null>(null);

    const sortedCarts = useMemo(() => {
        return [...shoppingCarts].sort((a, b) => (a.isMisc ? -1 : 1) || a.name.localeCompare(b.name));
    }, [shoppingCarts]);

    const allSearchItems = useMemo(() => {
        if (!searchQuery.trim()) return [];
        const lowerQuery = searchQuery.toLowerCase();
        
        return shoppingCarts.flatMap(cart => 
            cart.ingredients.map(ing => ({
                ...ing,
                cartId: cart.id,
                cartName: cart.name,
                cartCategory: cart.category || 'General',
                uniqueKey: `${cart.id}-${ing.name}-${ing.unit}`
            }))
        ).filter(item => item.name.toLowerCase().includes(lowerQuery));
    }, [shoppingCarts, searchQuery]);

    const handleAddItemToActive = async (item: Ingredient & { cartId?: string }) => {
        if (!activeCart || !onUpdateActiveCart) return;
        
        setIsAddingId(item.uniqueKey);
        try {
            const nextItems = [...activeCart.items];
            const category = item.cartId ? toListPillarId(item.cartId) : 'General';
            const itemId = `${item.name.toLowerCase().trim()}_${item.unit}_${category}`;
            const existingIndex = nextItems.findIndex(i => i.id === itemId);
            
            const newSource = {
                id: `search-${Date.now()}`,
                originName: 'List Search',
                quantity: item.quantity,
                originType: 'manual' as const
            };

            if (existingIndex > -1) {
                nextItems[existingIndex] = {
                    ...nextItems[existingIndex],
                    done: false,
                    sources: addManualQuantity(nextItems[existingIndex].sources, item.quantity, 'List Search'),
                };
            } else {
                nextItems.push({
                    id: itemId,
                    name: item.name,
                    unit: item.unit,
                    isMeat: item.isMeat,
                    done: false,
                    category,
                    sources: [newSource]
                });
            }
            await onUpdateActiveCart({ ...activeCart, items: nextItems });
        } finally {
            setIsAddingId(null);
        }
    };

    const handleDeleteItem = async (cartId: string, ingredientName: string) => {
        if (!onUpdateCart) return;
        const cart = shoppingCarts.find(c => c.id === cartId);
        if (!cart) return;
        
        if (window.confirm(t('delete') + '?')) {
            const nextIngredients = cart.ingredients.filter(i => i.name !== ingredientName);
            await onUpdateCart({ ...cart, ingredients: nextIngredients });
        }
    };

    const handleMoveItem = async (targetCartId: string) => {
        if (!moveItemData || !onUpdateCart) return;
        
        const sourceCart = shoppingCarts.find(c => c.id === moveItemData.sourceCartId);
        const targetCart = shoppingCarts.find(c => c.id === targetCartId);
        
        if (sourceCart && targetCart) {
            const nextSourceIngs = sourceCart.ingredients.filter(i => i.name !== moveItemData.ingredient.name);
            await onUpdateCart({ ...sourceCart, ingredients: nextSourceIngs });
            
            const nextTargetIngs = [...targetCart.ingredients, moveItemData.ingredient];
            await onUpdateCart({ ...targetCart, ingredients: nextTargetIngs });
        }
        setMoveItemData(null);
    };

    return (
        <div className="flex flex-col h-full animate-fade-in overflow-hidden bg-white dark:bg-slate-950">
            <div className="flex-shrink-0 bg-white dark:bg-slate-950 sticky top-0 z-10 border-b border-slate-200 dark:border-slate-800 shadow-sm transition-colors">
                <div className="px-6 pt-6 pb-4 flex flex-col gap-3">
                    <div className="flex gap-3">
                        <SearchField
                            className="flex-grow"
                            value={searchQuery}
                            onChange={setSearchQuery}
                            placeholder={t('search')}
                            clearLabel={t('clearSearch')}
                            iconClassName="w-5 h-5"
                            inputClassName="py-3.5 dark:bg-slate-900 focus:ring-brand-orange dark:placeholder:text-slate-600 shadow-sm"
                        />
                        
                        {canManage && !searchQuery && (
                            <button 
                                onClick={onAddCart}
                                className="p-3.5 bg-brand-orange text-white rounded-2xl shadow-lg shadow-brand-orange/20 active:scale-95 transition-all hover:bg-orange-600 flex-shrink-0 flex items-center justify-center aspect-square"
                            >
                                <PlusIcon className="w-6 h-6" />
                            </button>
                        )}
                    </div>
                </div>
                
                {!searchQuery && (
                    <div className="px-6 pb-2">
                        <p className="text-xs text-slate-400 dark:text-slate-500 font-black uppercase tracking-[0.2em]">
                            {sortedCarts.length} {t('lists')}
                        </p>
                    </div>
                )}
                
                {searchQuery && (
                    <div className="px-4 py-2 bg-gray-50/50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-800">
                        <p className="text-xs font-bold text-gray-400 dark:text-slate-500 tracking-wide uppercase">
                            {t('searchResultsCount').replace('{n}', allSearchItems.length.toString())}
                        </p>
                    </div>
                )}
            </div>

            <div className="flex-grow overflow-y-auto pb-32 scrollbar-none bg-slate-50 dark:bg-slate-950">
                {searchQuery ? (
                    allSearchItems.length > 0 ? (
                        <div className="bg-white dark:bg-slate-900 min-h-full">
                            {allSearchItems.map(item => (
                                <SearchResultItem
                                    key={item.uniqueKey}
                                    item={item}
                                    onAdd={() => handleAddItemToActive(item)}
                                    onDelete={() => handleDeleteItem(item.cartId, item.name)}
                                    onMove={() => setMoveItemData({ ingredient: item, sourceCartId: item.cartId })}
                                    isAdding={isAddingId === item.uniqueKey}
                                    activeCartItem={findActiveCartItemForListIngredient(activeCart, item)}
                                    language={language}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="py-20 text-center flex flex-col items-center gap-4">
                            <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-full text-slate-300 dark:text-slate-600">
                                <SearchIcon className="w-8 h-8" />
                            </div>
                            <div>
                                <p className="text-slate-500 dark:text-slate-400 font-bold text-sm mb-2">{t('noItemsInSearch')}</p>
                                <button 
                                    onClick={onAddCart}
                                    className="text-xs font-black uppercase tracking-widest text-brand-orange hover:underline"
                                >
                                    {t('createItem')}
                                </button>
                            </div>
                        </div>
                    )
                ) : (
                    <div className="px-4 py-4 space-y-3">
                        {sortedCarts.length === 0 ? (
                            <div className="py-20 text-center bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800">
                                <CartIcon className="w-12 h-12 text-slate-200 dark:text-slate-700 mx-auto mb-4" />
                                <p className="text-slate-400 dark:text-slate-500 font-bold">{t('noResults')}</p>
                            </div>
                        ) : (
                            sortedCarts.map(cart => {
                                const style = getCategoryDetails(cart.category || 'General');
                                return (
                                    <div 
                                        key={cart.id} 
                                        onClick={() => onViewCart(cart)}
                                        className="flex flex-row items-center justify-between p-4 bg-white dark:bg-slate-900 rounded-2xl border border-gray-100 dark:border-slate-800 shadow-sm hover:shadow-md transition-all cursor-pointer group"
                                    >
                                        <div className="flex flex-row items-center gap-4 min-w-0">
                                            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${style.bg} ${style.text}`}>
                                                {style.icon}
                                            </div>
                                            <div className="flex flex-col min-w-0">
                                                <h4 className="text-lg font-bold text-slate-800 dark:text-slate-100 truncate leading-tight">
                                                    {cart.name}
                                                </h4>
                                                <div className="flex items-center gap-2 mt-1.5">
                                                    {cart.category && (
                                                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider ${style.tagBg} ${style.tagText}`}>
                                                            {t(cart.category.toLowerCase() as any)}
                                                        </span>
                                                    )}
                                                    <span className="text-slate-300 dark:text-slate-600">•</span>
                                                    <span className="text-xs font-medium text-slate-400 dark:text-slate-500">
                                                        {cart.ingredients.length} {t('itemsLabel')}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="ltr:ml-2 rtl:mr-2 text-slate-300 dark:text-slate-600 group-hover:text-slate-400 dark:group-hover:text-slate-500 transition-colors">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5 rtl:rotate-180">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                            </svg>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </div>

            <MoveItemModal 
                isOpen={!!moveItemData}
                onClose={() => setMoveItemData(null)}
                carts={shoppingCarts.filter(c => c.id !== moveItemData?.sourceCartId)}
                onSelect={handleMoveItem}
                language={language}
            />
        </div>
    );
};

export default ListTab;